// Global variable to store ICP requirements
// This bypasses React state update issues
const globalICPRequirements = [];

/**
 * Custom hook for managing centralized ICP requirements
 * This hook provides a central repository for ICP requirements that can be
 * accessed and updated by all strategy components
 */
const useICPRequirements = () => {
  /**
   * Add or update ICP requirements from a strategy
   * @param {Array} newRequirements - New requirements to add or update
   * @param {string} strategyName - Name of the strategy providing the requirements
   */
  const updateRequirements = (newRequirements, strategyName) => {
    if (!newRequirements || !Array.isArray(newRequirements) || newRequirements.length === 0) {
      console.log(`No requirements to update from ${strategyName} strategy`);
      return;
    }

    console.log(`Updating ICP requirements from ${strategyName} strategy:`, newRequirements.length);
    
    // Log the first few requirements for debugging
    if (newRequirements.length > 0) {
      console.log('Sample requirements to add:', newRequirements.slice(0, 3).map(req => ({
        section_name: req.section_name,
        bool_str: req.bool_str ? req.bool_str.substring(0, 50) + '...' : 'undefined'
      })));
    }

    console.log('Previous requirements count:', globalICPRequirements.length);
    
    // Process each new requirement
    newRequirements.forEach(newReq => {
      // Skip requirements with missing section_name or bool_str
      if (!newReq.section_name || !newReq.bool_str) {
        console.warn('Skipping invalid requirement:', newReq);
        return;
      }
      
      // Check if this requirement already exists (by section_name)
      const existingIndex = globalICPRequirements.findIndex(
        req => req.section_name === newReq.section_name
      );
      
      if (existingIndex >= 0) {
        // If it exists, update it (merge bool_str if needed)
        const existingReq = globalICPRequirements[existingIndex];
        
        // If bool_str is different, we need to merge them
        if (existingReq.bool_str !== newReq.bool_str) {
          console.log(`Merging requirement for ${newReq.section_name}`);
          console.log(`Existing: ${existingReq.bool_str.substring(0, 50)}...`);
          console.log(`New: ${newReq.bool_str.substring(0, 50)}...`);
          
          // Simple merge strategy: combine with OR if they're different
          globalICPRequirements[existingIndex] = {
            ...newReq,
            bool_str: `(${existingReq.bool_str}) OR (${newReq.bool_str})`,
            // Add metadata about the source
            _sources: [...(existingReq._sources || [existingReq._source || 'unknown']), strategyName]
          };
        }
      } else {
        // If it doesn't exist, add it
        globalICPRequirements.push({
          ...newReq,
          // Add metadata about the source
          _source: strategyName,
          _sources: [strategyName]
        });
      }
    });
    
    console.log('Updated requirements count:', globalICPRequirements.length);
    
    // Log the first few updated requirements for debugging
    if (globalICPRequirements.length > 0) {
      console.log('Sample updated requirements:', globalICPRequirements.slice(0, 3).map(req => ({
        section_name: req.section_name,
        bool_str: req.bool_str ? req.bool_str.substring(0, 50) + '...' : 'undefined',
        _source: req._source
      })));
    }
  };

  /**
   * Get requirements for a specific section name
   * @param {string} sectionName - The section name to filter by
   * @returns {Array} Filtered requirements
   */
  const getRequirementsBySection = (sectionName) => {
    return globalICPRequirements.filter(req => req.section_name === sectionName);
  };

  /**
   * Get all requirements
   * @returns {Array} All ICP requirements
   */
  const getAllRequirements = () => {
    console.log('getAllRequirements called, returning:', globalICPRequirements.length, 'requirements');
    return [...globalICPRequirements]; // Return a copy to avoid reference issues
  };

  /**
   * Clear all requirements
   */
  const clearRequirements = () => {
    globalICPRequirements.length = 0; // Clear the array
  };

  return {
    icpRequirements: globalICPRequirements,
    updateRequirements,
    getRequirementsBySection,
    getAllRequirements,
    clearRequirements
  };
};

export default useICPRequirements;
